import { AnimatePresence, motion } from "framer-motion"
import { useEffect, useMemo, useState } from "react"
import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"

import useLayoutStore from "../../../store/useLayoutStore"

const clamp = (n, min, max) => Math.min(max, Math.max(min, Number.isNaN(n) ? min : n))

const parseVreme = (vreme) => {
    if (!vreme || typeof vreme !== "string") return { hour: 0, minute: 0 }
    const [h, m] = vreme.split(":").map(Number)
    return { hour: clamp(h, 0, 23), minute: clamp(m, 0, 59) }
}

const timeToMinutes = (vreme) => {
    const { hour, minute } = parseVreme(vreme)
    return hour * 60 + minute
}

const clampTimeParts = (hour, minute, minM, maxM) => {
    const total = clamp(hour * 60 + minute, minM, maxM)
    return { hour: Math.floor(total / 60), minute: total % 60 }
}

const formatVreme = (hour, minute) =>
    `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`

const EmployeeDayLogTimeEditPopover = ({
    open,
    modalContainerRef,
    initialVreme,
    prevVreme,
    nextVreme,
    onClose,
    onSave
}) => {
    const { t } = useTranslation("stats")
    const isDarkTheme = useLayoutStore((state) => state.isDarkTheme)
    const [hour, setHour] = useState(0)
    const [minute, setMinute] = useState(0)
    const [portalTarget, setPortalTarget] = useState(null)

    const { minM, maxM } = useMemo(() => {
        const min = prevVreme ? timeToMinutes(prevVreme) : 0
        const max = nextVreme ? timeToMinutes(nextVreme) : 23 * 60 + 59
        return { minM: min, maxM: Math.max(min, max) }
    }, [prevVreme, nextVreme])

    const minHour = Math.floor(minM / 60)
    const maxHour = Math.floor(maxM / 60)
    const minuteMin = hour === minHour ? minM % 60 : 0
    const minuteMax = hour === maxHour ? maxM % 60 : 59

    useEffect(() => {
        setPortalTarget(modalContainerRef?.current ?? null)
    }, [modalContainerRef])

    useEffect(() => {
        if (!open) return
        const parsed = parseVreme(initialVreme)
        const clamped = clampTimeParts(parsed.hour, parsed.minute, minM, maxM)
        setHour(clamped.hour)
        setMinute(clamped.minute)
    }, [open, initialVreme, minM, maxM])

    useEffect(() => {
        if (!open) return
        const onKey = (e) => {
            if (e.key === "Escape") {
                e.stopPropagation()
                onClose()
            }
        }
        document.addEventListener("keydown", onKey, true)
        return () => document.removeEventListener("keydown", onKey, true)
    }, [open, onClose])

    const applyTime = (nextHour, nextMinute) => {
        const clamped = clampTimeParts(nextHour, nextMinute, minM, maxM)
        setHour(clamped.hour)
        setMinute(clamped.minute)
    }

    const handleSave = () => {
        onSave?.(formatVreme(hour, minute))
    }

    if (!portalTarget) return null

    const darkLayer = isDarkTheme ? " asv-emp-log-time-edit-layer--dark" : ""
    const darkPanel = isDarkTheme ? " asv-emp-log-time-edit--dark" : ""

    return createPortal(
        <AnimatePresence>
            {open && (
                <motion.div
                    key="asv-emp-log-time-edit-layer"
                    className={`asv-emp-log-time-edit-layer${darkLayer}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.14, ease: "easeOut" }}
                >
                    <button
                        type="button"
                        className="asv-emp-log-time-edit-backdrop"
                        aria-label={t("timeEdit.closeAria")}
                        onClick={onClose}
                    />
                    <motion.div
                        className={`asv-emp-log-time-edit${darkPanel}`}
                        role="dialog"
                        aria-modal="true"
                        aria-label={t("timeEdit.dialogAria")}
                        initial={{ opacity: 0, scale: 0.94 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.94 }}
                        transition={{ duration: 0.14, ease: "easeOut" }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <p className="asv-emp-log-time-edit-title">{t("timeEdit.title")}</p>
                        <div className="asv-emp-log-time-edit-fields">
                            <label className="asv-emp-log-time-edit-field">
                                <span>{t("timeEdit.hour")}</span>
                                <input
                                    type="number"
                                    min={minHour}
                                    max={maxHour}
                                    value={hour}
                                    onChange={(e) => applyTime(clamp(e.target.valueAsNumber, minHour, maxHour), minute)}
                                    aria-label={t("timeEdit.hour")}
                                />
                            </label>
                            <span className="asv-emp-log-time-edit-sep" aria-hidden>
                                :
                            </span>
                            <label className="asv-emp-log-time-edit-field">
                                <span>{t("timeEdit.minuteShort")}</span>
                                <input
                                    type="number"
                                    min={minuteMin}
                                    max={minuteMax}
                                    value={minute}
                                    onChange={(e) =>
                                        applyTime(hour, clamp(e.target.valueAsNumber, minuteMin, minuteMax))
                                    }
                                    aria-label={t("timeEdit.minuteAria")}
                                />
                            </label>
                        </div>
                        <div className="asv-emp-log-time-edit-actions">
                            <button
                                type="button"
                                className="asv-emp-log-time-edit-btn asv-emp-log-time-edit-btn--ghost"
                                onClick={onClose}
                            >
                                {t("timeEdit.cancel")}
                            </button>
                            <button
                                type="button"
                                className="asv-emp-log-time-edit-btn asv-emp-log-time-edit-btn--primary"
                                onClick={handleSave}
                            >
                                {t("timeEdit.save")}
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>,
        portalTarget
    )
}

export default EmployeeDayLogTimeEditPopover
